import {
  IngestRejectionsProps,
  IngestRejectionsRows,
} from "../../../Pages/admin/Ingest/IngestIndex/IngestRejected/ingest-rejected";
import {
  IngestRequestedProps,
  IngestRequestedRows,
} from "../../../Pages/admin/Ingest/IngestIndex/IngestRequested/ingest-requested";
import { Distinct } from "../../../Utils/helpers";
import { GetMetadataByTagName } from "../../Utils/asset-object-utils";
import ApiClient from "../api-client";
import { cacheService, EnvType } from "../services";
import { CheckRequestsExistsResponse } from "../Types/check-requests-exists-response";
import { CreatePersonalBackupsRequest } from "../Types/create-personal-backups-request";
import { DbAssetObjectInfos } from "../Types/db-asset-object-infos";
import { DbIngestConfigColumnSetInfos } from "../Types/db-ingest-config-column-set-infos";
import { ConfigPackNameDto, DbIngestConfigPackInfos } from "../Types/db-ingest-config-pack-infos";
import {
  DbIngestConfigPackPlaceholderRowInfos,
  PlaceholderNameDto,
} from "../Types/db-ingest-config-pack-placeholder-row-infos";
import { DbIngestRequestInfos } from "../Types/db-ingest-request-infos";
import { ProviderNewAssignmentDto } from "../Types/db-lab-provider-infos";
import { DbMetadataFieldInfos } from "../Types/db-metadata-field-infos";
import { DeleteObjectRequest } from "../Types/delete-object-request";
import { GenericResponseStd } from "../Types/generic-response-std";
import { GetConfigPackLayoutResponse } from "../Types/get-config-pack-layout-response";
import { GetConfigPackListResponse } from "../Types/get-config-pack-list-response";
import { GetFilteredPlaceholdersRequest } from "../Types/get-filtered-placeholders-request";
import { GetIngestMetadataFieldsResponse } from "../Types/get-ingest-metadata-fields-response";
import { GetLabProviderInfosResponse } from "../Types/get-lab-provider-infos-response";
import { GetLabProviderResponse } from "../Types/get-lab-provider-response";
import { GetMyPersonalBackupResponse } from "../Types/get-my-personal-backup-response";
import { GetMyPersonalBackupsResponse } from "../Types/get-my-personal-backups-response";
import { GetPlaceholderResponse } from "../Types/get-placeholder-response";
import { GetPlaceholdersResponse } from "../Types/get-placeholders-response";
import { GetRejectionsByProviderResponse } from "../Types/get-rejections-by-provider-response";
import { GetRejectionsRatesResponse } from "../Types/get-rejections-rates-response";
import { GetRejectionsResponse } from "../Types/get-rejections-response";
import { GetRequestsResponse } from "../Types/get-requests-response";
import { MovePlaceholderByIdsRequest } from "../Types/move-placeholder-by-ids-request";
import { NormalizerInspectResponse } from "../Types/normalizer-inspect-response";
import { NormalizerProcessRequest } from "../Types/normalizer-process-request";
import { NormalizerProcessResponse } from "../Types/normalizer-process-response";
import { ObjectStatus } from "../Types/object-status";
import { ProviderAndAsperaConfiguration } from "../Types/provider-and-aspera-configuration";
import { UpdatePersonalBackupsRequest } from "../Types/update-personal-backups-request";
import { UpdatePlaceholderByIdsRequest } from "../Types/update-placeholder-by-ids-request";
import { default as IIngestApiClient, default as IngestApiClient } from "./ingest-api-client";

export default class IngestService {
  ingestApiClient: IIngestApiClient;

  constructor(envType: EnvType) {
    this.ingestApiClient = new IngestApiClient(new ApiClient(envType), envType);
  }

  async GetPlaceholderNameDtoList(packId: number): Promise<PlaceholderNameDto[]> {
    const placeholders = (await this.ingestApiClient.GetPlaceholdersByPackId(packId)).pack?.placeholderRows;

    if (!placeholders) return [];

    return placeholders.map((placeholder) => ({
      id: placeholder.id,
      name: placeholder.name || "-",
    }));
  }

  async GetConfigPackNameDtoList(): Promise<ConfigPackNameDto[]> {
    const configPacks = (await this.ingestApiClient.GetConfigPackList()).packs;

    if (!configPacks) return [];

    return configPacks.map((pack) => ({
      id: pack.id,
      name: pack.name || "-",
    }));
  }

  async RemindProvider(assetId: number): Promise<GenericResponseStd> {
    return this.ingestApiClient.RemindProvider(assetId);
  }

  async UploadAndReplaceRequestMetadata(
    objectId: number,
    fileToUpload: File,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<GenericResponseStd> {
    return this.ingestApiClient.UploadAndReplaceRequestMetadata(
      objectId,
      fileToUpload,
      abortSignal,
      onUploadProgressCallback
    );
  }

  async AssignRequestToProvider(requestId: number, newProviderId: number): Promise<GenericResponseStd> {
    return this.ingestApiClient.AssignRequestToProvider(requestId, newProviderId);
  }

  async GetNewAssignmentProviders(): Promise<ProviderNewAssignmentDto[]> {
    const response = await this.ingestApiClient.GetProviders();

    if (!response.providers) return [];

    return response.providers.map((provider) => ({
      id: provider.id,
      name: provider.labName || "-",
    }));
  }

  async GetRequestedIngestWorkflowsWithProviders(): Promise<[IngestRequestedProps, ProviderNewAssignmentDto[]]> {
    const providersInfos = await this.GetNewAssignmentProviders();

    const requestsInfos = (await this.ingestApiClient.GetRequests()).assets;
    const computedInstances = this.MapInstanceInfosToIngestRequestedRows(requestsInfos, providersInfos);

    const data: IngestRequestedProps = {
      rows: computedInstances,
      filtersData: {
        createdByNames: computedInstances
          .map((x) => x.createdByUserName)
          .filter(Distinct)
          .sort(),
        providersNames: computedInstances
          .map((x) => x.providerName)
          .filter(Distinct)
          .sort(),
      },
    };
    return [data, providersInfos];
  }

  public GetRequestedIngestWorkflowsComputed(
    request: DbIngestRequestInfos,
    providers: ProviderNewAssignmentDto[]
  ): IngestRequestedRows {
    const computedInstance = this.MapInstanceInfosToIngestRequestedRows([request], providers);
    return computedInstance[0];
  }

  async GetRejectedIngestWorkflowsWithProviders(): Promise<[IngestRejectionsProps | null]> {
    const rejectionsInfos = (await this.ingestApiClient.GetRejections()).assets;
    if (rejectionsInfos) {
      const computedInstances = this.MapInstanceInfosToIngestRejectionsRows(rejectionsInfos);

      const data: IngestRejectionsProps = {
        rows: computedInstances,
        filtersData: {
          class: computedInstances
            .map((x) => x.assetClass)
            .filter(Distinct)
            .sort(),
          rejectedBy: computedInstances
            .filter((x) => x !== null)
            .map((x) => x.rejectedBy!)
            .filter(Distinct)
            .sort(),
          type: computedInstances
            .map((x) => x.assetType)
            .filter(Distinct)
            .sort(),
        },
      };
      return [data];
    }
    return [null];
  }

  async DeleteRequest(request: DeleteObjectRequest): Promise<GenericResponseStd> {
    return await this.ingestApiClient.DeleteRequest(request);
  }
  //------PRIVATE------
  private MapInstanceInfosToIngestRequestedRows(
    requestsInfos: DbIngestRequestInfos[],
    providerInfos: ProviderNewAssignmentDto[]
  ): IngestRequestedRows[] {
    if (requestsInfos === null || requestsInfos.length === 0) return [];

    const metadatasInfos: DbMetadataFieldInfos[] = cacheService.GetMetadata();
    let mappedData: IngestRequestedRows[] = [];

    requestsInfos.forEach((requestInfos) => {
      mappedData.push({
        id: requestInfos.id,
        fileSize: requestInfos.fileSize,
        ingestStatus: requestInfos.processingState,
        createdByUserName: requestInfos.createdByUserName || "-",
        assetId: requestInfos.request!.id,
        assetParentTitleId: requestInfos.request!.parentTitleId,
        assetParentTitleName: requestInfos.titleName || "Drive",
        assetType: requestInfos.request!.type,
        assetClass: requestInfos.request!.classification!,
        assetStatus: requestInfos.request!.status,
        assetParentName: requestInfos.parentName || "-",
        assetName: requestInfos.request!.name,
        assetAnnexFormatIds: requestInfos.uploadableFileFormatIds || [],
        assetIsHdr: GetMetadataByTagName(requestInfos.request!, "isHdr", metadatasInfos)?.value || false,
        assetProviderId: requestInfos.request!.providerId!,
        createdAt: requestInfos.request!.creationInfos!.date!.toString(),
        providerName: providerInfos.find((x) => x.id === requestInfos.request!.providerId)?.name || "-",
        followersIds: requestInfos.request!.followersInfos?.flatMap((x) => x.userId!) || [],
        lockedOrderItemIds: requestInfos.undoableOrderItemsIds,
        comment: GetMetadataByTagName(requestInfos.request, "comment")?.value ?? null,
        retries: GetMetadataByTagName(requestInfos.request, "ingestWorkflowCount")?.value ?? 1,
        placeholderName: requestInfos.placeholderName,
        custom: {
          billingStatus: GetMetadataByTagName(requestInfos.request, "billingStatus")?.value ?? null,
          ingestStatus: GetMetadataByTagName(requestInfos.request, "ingestStatus")?.value ?? null,
          woId: GetMetadataByTagName(requestInfos.request, "btId")?.value ?? null,
        },
        asset: requestInfos.request ?? null,
      });
    });

    return mappedData;
  }

  private MapInstanceInfosToIngestRejectionsRows(rejectionsInfos: DbAssetObjectInfos[]): IngestRejectionsRows[] {
    if (rejectionsInfos === null || rejectionsInfos.length === 0) return [];
    let mappedData: IngestRejectionsRows[] = [];

    rejectionsInfos.forEach((data) => {
      let rejectedBy: string | null = null;
      let message: string[] | null = null;

      if (data.statusHistory) {
        let match = data.statusHistory
          .reverse()
          .find((x) => x.status === ObjectStatus.Rejected || x.status === ObjectStatus.RejectedAuto);
        rejectedBy = match?.userName!;
        message = match?.comment?.split(",") ?? [];
      }

      if (message && message.length > 1 && data.status === ObjectStatus.RejectedAuto) {
        let indexOfMark = message[0].indexOf(":") + 1;
        let splitedStart = message[0].slice(0, indexOfMark);
        message.splice(0, 1, splitedStart, message[0].slice(indexOfMark));
      }

      if (!message || message.length === 0)
        message = GetMetadataByTagName(data, "ingestResultMessage")?.value?.split(",") || [];

      mappedData.push({
        id: data.id,
        assetId: data.id,
        assetType: data.type,
        assetClass: data.classification!,
        assetStatus: data.status!,
        assetName: data.name,
        createdAt: data.creationInfos?.date.toString() || null,
        createdByUserName: data.creationInfos?.userName ?? null,
        message: message,
        rejectedBy: rejectedBy,
        assetParentObjectName: data.parentObject?.name,
        assetParentTitleId: data.parentTitleId,
        assetParentTitleName: data.parentTitleName || "Drive",
        followersIds: data.followersInfos?.flatMap((x) => x.userId!) || [],
        assetProviderId: data.providerId!,
        assetProviderName: data.providerName ? data.providerName : "-",
        rejectionDate: data.statusHistory?.at(-1)?.date?.toString() || null,
        bypassAllowed: GetMetadataByTagName(data, "ingestQCAllowBypass")?.value ?? false,
        asset: data ?? null,
      });
    });

    return mappedData;
  }

  async GetAsperaSettingsForRequestUpload(): Promise<ProviderAndAsperaConfiguration[]> {
    const settings = (await this.ingestApiClient.GetAsperaSettings()).settings;
    return settings || [];
  }

  //------DO NOT USE------

  async GetRequests(): Promise<GetRequestsResponse> {
    return await this.ingestApiClient.GetRequests();
  }
  async GetMyRequests(): Promise<GetRequestsResponse> {
    return await this.ingestApiClient.GetMyRequests();
  }
  async CheckRequestsExists(titleId: number): Promise<CheckRequestsExistsResponse> {
    return await this.ingestApiClient.CheckRequestsExists(titleId);
  }
  async ReprocessRequestAfterAutomatedRejection(id: number): Promise<GenericResponseStd> {
    return await this.ingestApiClient.ReprocessRequestAfterAutomatedRejection(id);
  }
  async GetRejections(): Promise<GetRejectionsResponse> {
    return await this.ingestApiClient.GetRejections();
  }
  async GetRejectionsByProviderId(labProviderId: number): Promise<GetRejectionsByProviderResponse> {
    return await this.ingestApiClient.GetRejectionsByProviderId(labProviderId);
  }
  async GetProviderById(labProviderId: number): Promise<GetLabProviderInfosResponse> {
    return await this.ingestApiClient.GetProviderById(labProviderId);
  }
  async GetProviders(): Promise<GetLabProviderResponse> {
    return await this.ingestApiClient.GetProviders();
  }
  async GetRejectionsRatings(maxDays: number = 180): Promise<GetRejectionsRatesResponse> {
    return await this.ingestApiClient.GetRejectionsRatings(maxDays);
  }
  async ReloadProviders(): Promise<GenericResponseStd> {
    return await this.ingestApiClient.ReloadProviders();
  }
  async GetIngestMetadataFields(): Promise<GetIngestMetadataFieldsResponse> {
    return await this.ingestApiClient.GetIngestMetadataFields();
  }
  async GetConfigPackList(): Promise<GetConfigPackListResponse> {
    return await this.ingestApiClient.GetConfigPackList();
  }
  async GetConfigPackById(packId: number): Promise<GetConfigPackLayoutResponse> {
    return await this.ingestApiClient.GetConfigPackById(packId);
  }
  async GetPlaceholdersByPackId(packId: number): Promise<GetConfigPackLayoutResponse> {
    return await this.ingestApiClient.GetPlaceholdersByPackId(packId);
  }
  async CreateConfigPack(pack: DbIngestConfigPackInfos): Promise<GenericResponseStd> {
    return await this.ingestApiClient.CreateConfigPack(pack);
  }
  async DeleteConfigPackById(id: number): Promise<GenericResponseStd> {
    return await this.ingestApiClient.DeleteConfigPackById(id);
  }
  async GetPlaceholders(): Promise<GetPlaceholdersResponse> {
    return await this.ingestApiClient.GetPlaceholders();
  }
  async GetFilteredPlaceholders(request: GetFilteredPlaceholdersRequest): Promise<GetPlaceholdersResponse> {
    return await this.ingestApiClient.GetFilteredPlaceholders(request);
  }
  async GetPlaceholderById(placeholderId: number): Promise<GetPlaceholderResponse> {
    return await this.ingestApiClient.GetPlaceholderById(placeholderId);
  }
  async CreatePlaceholder(packId: number, request: DbIngestConfigPackPlaceholderRowInfos): Promise<GenericResponseStd> {
    return await this.ingestApiClient.CreatePlaceholder(packId, request);
  }
  async UpdatePlaceholder(request: DbIngestConfigPackPlaceholderRowInfos): Promise<GenericResponseStd> {
    return await this.ingestApiClient.UpdatePlaceholder(request);
  }
  async UpdatePlaceholders(request: UpdatePlaceholderByIdsRequest): Promise<GenericResponseStd> {
    return await this.ingestApiClient.UpdatePlaceholders(request);
  }
  async MovePlaceholders(request: MovePlaceholderByIdsRequest): Promise<GenericResponseStd> {
    return await this.ingestApiClient.MovePlaceholders(request);
  }
  async DeletePlaceholderById(placeholderId: number): Promise<GenericResponseStd> {
    return await this.ingestApiClient.DeletePlaceholderById(placeholderId);
  }
  async GetMyPersonalBackups(): Promise<GetMyPersonalBackupsResponse> {
    return await this.ingestApiClient.GetMyPersonalBackups();
  }
  async GetMyPersonalBackupById(id: number): Promise<GetMyPersonalBackupResponse> {
    return await this.ingestApiClient.GetMyPersonalBackupById(id);
  }
  async CreatePersonalBackups(request: CreatePersonalBackupsRequest): Promise<GenericResponseStd> {
    return await this.ingestApiClient.CreatePersonalBackups(request);
  }
  async UpdatePersonalBackups(request: UpdatePersonalBackupsRequest): Promise<GenericResponseStd> {
    return await this.ingestApiClient.UpdatePersonalBackups(request);
  }
  async DeletePersonalBackups(id: number): Promise<GenericResponseStd> {
    return await this.ingestApiClient.DeletePersonalBackups(id);
  }
  async CreateIngestConfigColumnSet(column: DbIngestConfigColumnSetInfos): Promise<GenericResponseStd> {
    return await this.ingestApiClient.CreateIngestConfigColumnSet(column);
  }
  async UpdateIngestConfigColumnSet(column: DbIngestConfigColumnSetInfos): Promise<GenericResponseStd> {
    return await this.ingestApiClient.UpdateIngestConfigColumnSet(column);
  }
  async NormalizerGetRequests(): Promise<GetRequestsResponse> {
    return await this.ingestApiClient.NormalizerGetRequests();
  }
  async NormalizerInspect(objectId: number): Promise<NormalizerInspectResponse> {
    return await this.ingestApiClient.NormalizerInspect(objectId);
  }
  async NormalizeProcess(request: NormalizerProcessRequest): Promise<NormalizerProcessResponse> {
    return await this.ingestApiClient.NormalizeProcess(request);
  }

  //------Utils------
  GetServiceUrl(): string {
    return this.ingestApiClient.GetApiUrl();
  }
}
